import { useEffect, useMemo, useState } from "react";
import { BrandingLogo } from "@/components/branding/BrandingLogo";
import { CampsiteBrandBar } from "@/components/branding/CampsiteBrandBar";
import type { GuestTabletIdleScreen, GuestTabletIdleTextBlock } from "@/types/guestTablet";

type Props = {
  idleScreen: GuestTabletIdleScreen;
  siteName?: string;
  logoUrl?: string | null;
  workstationLabel?: string;
  /** Ersetzt den Standard-Arbeitsplatz-Hinweis (z. B. Infobildschirm). */
  footerHint?: string | null;
  showFooterHint?: boolean;
  /** Vollempfangene Startseite des Gäste-Tablets. Infobildschirme bleiben beim klassischen Layout. */
  variant?: "classic" | "welcome";
};

export function TabletIdleScreen(props: Props) {
  if (props.variant === "welcome") {
    return <TabletWelcomeIdle {...props} />;
  }
  return <TabletIdleScreenClassic {...props} />;
}

function TabletIdleScreenClassic({
  idleScreen,
  siteName,
  logoUrl,
  workstationLabel = "",
  footerHint,
  showFooterHint,
}: Props) {
  const displayFooter = showFooterHint ?? idleScreen.show_workstation_hint;
  const images = useMemo(
    () => [...idleScreen.images].sort((a, b) => a.sort_order - b.sort_order),
    [idleScreen.images],
  );
  const textBlocks = useMemo(
    () => [...idleScreen.text_blocks].sort((a, b) => a.sort_order - b.sort_order),
    [idleScreen.text_blocks],
  );

  const [slideIndex, setSlideIndex] = useState(0);
  const intervalMs = Math.max(3, idleScreen.slideshow_interval_seconds) * 1000;

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = window.setInterval(() => {
      setSlideIndex((i) => (i + 1) % images.length);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [images.length, intervalMs]);

  useEffect(() => {
    if (slideIndex >= images.length) setSlideIndex(0);
  }, [images.length, slideIndex]);

  const hasSlideshow = images.length > 0;

  return (
    <div
      className={
        hasSlideshow
          ? "guest-tablet-idle-screen guest-tablet-idle-screen--split"
          : "guest-tablet-idle-screen guest-tablet-idle-screen--centered"
      }
      aria-live="polite"
    >
      {siteName ? (
        <CampsiteBrandBar
          siteName={siteName}
          logoUrl={logoUrl}
          className="guest-tablet-idle-brand shrink-0 border-b border-separator/60"
          compact
        />
      ) : null}
      {hasSlideshow ? (
        <div className="guest-tablet-idle-slideshow" aria-label="Bildergalerie">
          {images.map((img, idx) => (
            <figure
              key={img.id}
              className={
                idx === slideIndex
                  ? "guest-tablet-idle-slide guest-tablet-idle-slide--active"
                  : "guest-tablet-idle-slide"
              }
            >
              <img src={img.image_url} alt={img.alt_text || ""} loading="lazy" />
              {img.caption ? (
                <figcaption className="guest-tablet-idle-slide-caption">{img.caption}</figcaption>
              ) : null}
            </figure>
          ))}
          {images.length > 1 ? (
            <div className="guest-tablet-idle-dots" role="tablist" aria-label="Folie wählen">
              {images.map((img, idx) => (
                <button
                  key={img.id}
                  type="button"
                  role="tab"
                  aria-selected={idx === slideIndex}
                  aria-label={`Bild ${idx + 1} von ${images.length}`}
                  className={
                    idx === slideIndex
                      ? "guest-tablet-idle-dot guest-tablet-idle-dot--active"
                      : "guest-tablet-idle-dot"
                  }
                  onClick={() => setSlideIndex(idx)}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="guest-tablet-idle-content">
        {textBlocks.map((block) => (
          <article
            key={block.id}
            className={`guest-tablet-idle-text guest-tablet-idle-text--${block.variant}`}
          >
            {block.title ? <h2 className="guest-tablet-idle-text-title">{block.title}</h2> : null}
            <p className="guest-tablet-idle-text-body whitespace-pre-line">{block.body}</p>
          </article>
        ))}

        {displayFooter && (footerHint || workstationLabel) ? (
          <p className="guest-tablet-idle-workstation-hint">
            {footerHint ?? `${workstationLabel} · Bereit für den nächsten Gast`}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function WelcomeClock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const time = new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(now);
  const date = new Intl.DateTimeFormat("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(now);

  return (
    <div className="guest-tablet-welcome__clock">
      <p className="guest-tablet-welcome__time">{time}</p>
      <p className="guest-tablet-welcome__date">{date}</p>
    </div>
  );
}

function TabletWelcomeIdle({
  idleScreen,
  siteName,
  logoUrl,
  workstationLabel = "",
  footerHint,
  showFooterHint,
}: Props) {
  const displayFooter = showFooterHint ?? idleScreen.show_workstation_hint;
  const images = useMemo(
    () => [...idleScreen.images].sort((a, b) => a.sort_order - b.sort_order),
    [idleScreen.images],
  );
  const textBlocks = useMemo(
    () => [...idleScreen.text_blocks].sort((a, b) => a.sort_order - b.sort_order),
    [idleScreen.text_blocks],
  );
  const headlines = textBlocks.filter((block) => block.variant === "headline");
  const highlights = textBlocks.filter((block) => block.variant === "highlight");
  const hints = textBlocks.filter((block) => block.variant === "hint");
  const bodies = textBlocks.filter((block) => block.variant === "body");
  const lead = headlines[0] ?? bodies[0] ?? null;
  const extraHeadlines = headlines.slice(lead && lead.variant === "headline" ? 1 : 0);
  const paragraphs = lead && lead.variant === "body" ? bodies.slice(1) : bodies;

  const [slideIndex, setSlideIndex] = useState(0);
  const intervalMs = Math.max(3, idleScreen.slideshow_interval_seconds) * 1000;
  const hasSlideshow = images.length > 0;
  const activeImage = images[slideIndex];

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = window.setInterval(() => {
      setSlideIndex((i) => (i + 1) % images.length);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [images.length, intervalMs]);

  useEffect(() => {
    if (slideIndex >= images.length) setSlideIndex(0);
  }, [images.length, slideIndex]);

  const statusText = footerHint ?? (workstationLabel ? `${workstationLabel} · Bereit für den nächsten Gast` : "");

  return (
    <section
      className={
        hasSlideshow
          ? "guest-tablet-welcome guest-tablet-welcome--photo"
          : "guest-tablet-welcome"
      }
      aria-label="Willkommen"
    >
      <div className="guest-tablet-welcome__atmosphere" aria-hidden="true">
        <span className="guest-tablet-welcome__orb guest-tablet-welcome__orb--a" />
        <span className="guest-tablet-welcome__orb guest-tablet-welcome__orb--b" />
        <span className="guest-tablet-welcome__orb guest-tablet-welcome__orb--c" />
      </div>

      {hasSlideshow ? (
        <div className="guest-tablet-welcome__slideshow" aria-hidden="true">
          {images.map((img, idx) => (
            <figure
              key={img.id}
              className={
                idx === slideIndex
                  ? "guest-tablet-welcome__slide guest-tablet-welcome__slide--active"
                  : "guest-tablet-welcome__slide"
              }
            >
              <img src={img.image_url} alt="" />
            </figure>
          ))}
          <div className="guest-tablet-welcome__scrim" />
        </div>
      ) : null}

      <header className="guest-tablet-welcome__header">
        {siteName ? (
          <div className="guest-tablet-welcome__brand">
            <BrandingLogo
              logoUrl={logoUrl}
              siteName={siteName}
              imgClassName="max-h-14 max-w-[11rem]"
              fallbackClassName="text-3xl"
            />
            <span className="guest-tablet-welcome__site">{siteName}</span>
          </div>
        ) : (
          <span />
        )}
        <WelcomeClock />
      </header>

      <div className="guest-tablet-welcome__stage">
        {activeImage?.caption ? (
          <p className="guest-tablet-welcome__eyebrow">{activeImage.caption}</p>
        ) : null}
        {lead ? <WelcomeCopy block={lead} as="h1" /> : null}
        {extraHeadlines.map((block) => (
          <WelcomeCopy key={block.id} block={block} />
        ))}
        {paragraphs.map((block) => (
          <WelcomeCopy key={block.id} block={block} />
        ))}
        {highlights.map((block) => (
          <WelcomeCopy key={block.id} block={block} />
        ))}
      </div>

      <footer className="guest-tablet-welcome__footer">
        {hints.length > 0 ? (
          <div className="guest-tablet-welcome__hints">
            {hints.map((block) => (
              <p key={block.id} className="guest-tablet-welcome__hint whitespace-pre-line">
                {block.title ? <span className="guest-tablet-welcome__hint-title">{block.title}</span> : null}
                {block.body}
              </p>
            ))}
          </div>
        ) : null}

        {images.length > 1 ? (
          <div className="guest-tablet-welcome__dots" role="tablist" aria-label="Folie wählen">
            {images.map((img, idx) => (
              <button
                key={img.id}
                type="button"
                role="tab"
                aria-selected={idx === slideIndex}
                aria-label={`Bild ${idx + 1} von ${images.length}`}
                className={
                  idx === slideIndex
                    ? "guest-tablet-welcome__dot guest-tablet-welcome__dot--active"
                    : "guest-tablet-welcome__dot"
                }
                onClick={() => setSlideIndex(idx)}
              />
            ))}
          </div>
        ) : null}

        {displayFooter && statusText ? (
          <p className="guest-tablet-welcome__status">
            <span className="guest-tablet-welcome__pulse" aria-hidden="true" />
            {statusText}
          </p>
        ) : null}
      </footer>
    </section>
  );
}

function WelcomeCopy({
  block,
  as,
}: {
  block: GuestTabletIdleTextBlock;
  as?: "h1";
}) {
  const Tag = as ?? (block.variant === "headline" ? "h2" : "p");
  return (
    <Tag className={`guest-tablet-welcome__copy guest-tablet-welcome__copy--${block.variant}`}>
      {block.title ? <span className="guest-tablet-welcome__copy-title">{block.title}</span> : null}
      <span className="whitespace-pre-line">{block.body}</span>
    </Tag>
  );
}
